import { useState } from 'react';
import {
  crisisResources,
  incidentContacts,
  incidentNotifyContact,
  incidentNotifyPreview,
  type IncidentNotifyPreview,
  type ApiClient,
  type ApiError,
  type CrisisResource,
  type CrisisType,
  type IncidentContact,
} from '@aithora/shared';
import { useQuery } from '../../useQuery';
import { sx } from '../../ui/designStyles';
import { ErrorState } from '../../ui/states';
import { ConfirmDialog } from '../../ui/ConfirmDialog';
import type { Case } from './model';

// Restored from the Flutter console's Emergency view (emergency_view.dart): who was paged
// for this emergency and who could be (with Notify), and the crisis lines. The V3 design's
// live Guardian has no control for either; its older tabbed Guardian (gOld, T4540-4680)
// drew both, so this is that markup: "Escalation ladder" and "Crisis lines".
// Each block draws nothing until its route answers with rows.

const ROLE_WORD: Record<string, string> = {
  ADMIN: 'facility admin',
  CAREGIVER: 'care staff',
  STAFF: 'care staff',
  FAMILY_MEMBER: 'family',
};

/** The crisis lines a kind of emergency needs; 988 and the elder abuse line (to APS) always. */
export function crisisTypesFor(kind: string): CrisisType[] {
  const k = kind.toLowerCase();
  const out: CrisisType[] = ['SELF_HARM', 'ABUSE'];
  if (/fraud|scam|money|financ|exploit/.test(k)) out.push('FRAUD');
  return out;
}

const hhmm = (iso: string | null) => {
  const t = iso ? Date.parse(iso) : NaN;
  return Number.isFinite(t) ? new Date(t).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : '';
};

async function loadCrisis(client: ApiClient, kind: string, signal: AbortSignal): Promise<CrisisResource[]> {
  const lists = await Promise.all(crisisTypesFor(kind).map((type) => crisisResources(client, { query: { type }, signal }).then((r) => r.resources).catch(() => [])));
  const seen = new Set<string>();
  return lists.flat().filter((r) => (seen.has(r.name) ? false : (seen.add(r.name), true)));
}

export function EmergencyExtras({ client, sel, canAct, disabled, title }: { client: ApiClient; sel: Case; canAct: boolean; disabled: boolean; title?: string }) {
  const contacts = useQuery((signal) => incidentContacts(client, { params: { id: sel.id }, signal }), [client, sel.id]);
  const crisis = useQuery((signal) => loadCrisis(client, sel.kind, signal), [client, sel.kind]);
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<ApiError | null>(null);

  const rows: IncidentContact[] = contacts.data?.contacts ?? [];
  const lines = crisis.data ?? [];
  const notifiedN = rows.filter((c) => c.notified).length;

  // Notify is a deliberate message to a named person (AF-1611, Andrew 2026-10-08): one
  // confirmation sheet naming who, their relationship and the channel, with the exact text.
  // Nothing is sent until Send now. Automatic escalations never come through here.
  const [sheet, setSheet] = useState<{ contact: IncidentContact; preview: IncidentNotifyPreview } | null>(null);
  const notify = async (c: IncidentContact) => {
    if (!canAct || disabled || busy) return;
    setBusy(c.recipientId);
    setErr(null);
    try {
      const preview = await incidentNotifyPreview(client, { query: { incidentId: sel.id, contactId: c.recipientId } });
      setSheet({ contact: c, preview });
    } catch (e) {
      setErr(e as ApiError);
    } finally {
      setBusy(null);
    }
  };
  const send = async () => {
    if (!sheet) return;
    await incidentNotifyContact(client, { body: { incidentId: sel.id, contactId: sheet.contact.recipientId, confirmed: true, text: sheet.preview.text } });
    contacts.reload();
  };

  if (!rows.length && !lines.length) return null;
  return (
    <>
      {rows.length > 0 && (
        <div data-testid="guardian-ladder" style={sx('min-width:0;border-top:1px solid rgba(34,39,46,.14);padding-top:8px;')}>
          <div style={sx('display:flex;align-items:center;gap:8px;flex-wrap:wrap;row-gap:4px;min-width:0;')}>
            <span style={sx('font-size:16px;font-weight:700;white-space:nowrap;')}>Escalation ladder</span>
            <span style={sx('flex:1 1 10px;')} />
            <span style={sx('font-size:14px;color:#54606C;font-variant-numeric:tabular-nums;white-space:nowrap;')}>{`${notifiedN} of ${rows.length} notified`}</span>
          </div>
          <div style={sx('font-size:14px;color:#54606C;margin-top:3px;line-height:1.4;text-wrap:pretty;')}>
            Who was paged for this emergency, and who else you can reach now. Notify pages them in the app and records your name.
          </div>
          {rows.map((c) => {
            const on = c.notified;
            return (
              <div key={c.recipientId} style={sx('display:flex;align-items:center;gap:10px;flex-wrap:wrap;row-gap:5px;padding:6px 0;border-top:1px solid rgba(34,39,46,.07);min-width:0;')}>
                <span style={sx('flex:1 1 110px;min-width:min(100%,110px);')}>
                  <span style={sx('display:block;font-size:14px;font-weight:700;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;')}>{c.name ?? 'Unnamed'}</span>
                  <span style={sx('display:block;font-size:14px;color:#54606C;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;')}>{ROLE_WORD[String(c.role ?? '').toUpperCase()] ?? String(c.role ?? '').toLowerCase()}</span>
                </span>
                <span style={sx('flex:0 1 128px;min-width:0;')}>
                  <span style={sx(`display:block;font-size:14px;font-weight:700;letter-spacing:.07em;font-variant-numeric:tabular-nums;color:${on ? '#1F6B52' : '#54606C'};white-space:nowrap;overflow:hidden;text-overflow:ellipsis;`)}>{on ? 'NOTIFIED' : 'not notified'}</span>
                  <span style={sx('display:block;font-size:14px;color:#54606C;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;')}>{on ? hhmm(c.notifiedAt) : 'no attempt recorded'}</span>
                </span>
                {on ? (
                  <span style={sx('display:inline-flex;align-items:center;justify-content:center;height:30px;padding:0 13px;border-radius:7px;font-size:14px;font-weight:600;flex:0 0 auto;white-space:nowrap;background:rgba(34,39,46,.06);color:#54606C;')}>Notified</span>
                ) : (
                  canAct && (
                    <button
                      type="button"
                      className="gd-a"
                      onClick={() => void notify(c)}
                      disabled={disabled || busy === c.recipientId}
                      aria-label={`Notify ${c.name ?? 'this contact'}`}
                      title={title}
                      style={sx('display:inline-flex;align-items:center;justify-content:center;height:30px;padding:0 13px;border-radius:7px;font-size:14px;font-weight:600;text-decoration:none;flex:0 0 auto;white-space:nowrap;background:#2F5C97;color:#fff;border:0;cursor:pointer;')}
                    >
                      Notify
                    </button>
                  )
                )}
              </div>
            );
          })}
          {err && <ErrorState error={err} />}
        </div>
      )}
      {sheet && (
        <ConfirmDialog
          title={`Send to ${sheet.preview.recipient.name ?? 'this contact'}?`}
          body={
            <>
              <span style={sx('display:block;')}>{`${sheet.preview.recipient.name ?? 'This contact'} (${sheet.preview.recipient.relationship}), by ${sheet.preview.recipient.channel}:`}</span>
              <span data-testid="notify-text" style={sx('display:block;margin-top:8px;padding:10px 12px;border-radius:8px;background:rgba(34,39,46,.06);font-size:16px;color:#22272E;line-height:1.45;')}>{sheet.preview.text}</span>
              <span style={sx('display:block;margin-top:8px;font-size:14px;color:#54606C;')}>Your name and the time are recorded with the message.</span>
            </>
          }
          confirmLabel="Send now"
          onConfirm={() => send()}
          onClose={() => setSheet(null)}
        />
      )}
      {lines.length > 0 && (
        <div data-testid="guardian-crisis" style={sx('min-width:0;border-top:1px solid rgba(34,39,46,.14);padding-top:8px;')}>
          <div style={sx('font-size:14px;font-weight:700;color:#54606C;')}>Crisis lines</div>
          <div style={sx('display:flex;flex-wrap:wrap;gap:6px;margin-top:7px;min-width:0;')}>
            {lines.map((c) => (
              <span key={c.name} title={c.url} style={sx('display:inline-flex;align-items:baseline;gap:6px;border:1px solid rgba(34,39,46,.12);border-radius:6px;padding:3px 8px;min-width:0;')}>
                <span style={sx('font-size:14px;font-weight:700;font-variant-numeric:tabular-nums;color:#2F5C97;white-space:nowrap;')}>{c.phone || c.text}</span>
                <span style={sx('font-size:14px;color:#54606C;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;')}>{c.name}</span>
              </span>
            ))}
          </div>
          <div style={sx('font-size:14px;color:#54606C;margin-top:6px;line-height:1.4;text-wrap:pretty;')}>A person makes these calls, never the system. The elder abuse line routes to your local Adult Protective Services.</div>
        </div>
      )}
    </>
  );
}
